/* Rugby header behaviour. Split out of sections/header.liquid so the section stays inside
   Shopify's upload envelope, and so the browser can cache it. Contains no Liquid. */
class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.setHeaderHeight();

      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      };

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach(disclosure => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal) this.searchModal.close(false);
    }
  }

  customElements.define('sticky-header', StickyHeader);

/* ---------------------------------------------------------------------------
   Mobile drawer stacking fix.

   The drawer is position:fixed at z-index 199, but none of its ancestors
   create a stacking context: .section-header and its Shopify wrapper are both
   position:static, so their z-index values are inert. That leaves the drawer
   competing directly in the root stacking layer against page content that is
   also positioned, and page sections later in the DOM won the paint order.
   The result was menu items and hero content drawn over each other.

   Fix: while the drawer is open, make .section-header a positioned element
   with a z-index above anything in <main>. That gives the whole header subtree,
   drawer included, its own stacking context that sits on top. Reverted on
   close so the non-sticky header behaviour is untouched.
   --------------------------------------------------------------------------- */
(function () {
  var TOP = '2147483000';

  function sync() {
    var host = document.querySelector('.section-header');
    if (!host) return;
    var open = document.documentElement.classList.contains('rh-drawer-open');

    if (open) {
      host.style.setProperty('position', 'relative', 'important');
      host.style.setProperty('z-index', TOP, 'important');
      var drawer = document.querySelector('.rh__drawer');
      if (drawer) drawer.style.setProperty('z-index', TOP, 'important');
    } else {
      host.style.removeProperty('position');
      host.style.removeProperty('z-index');
    }
  }

  function start() {
    sync();
    new MutationObserver(sync).observe(document.documentElement, {
      attributes: true, attributeFilter: ['class']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ---------------------------------------------------------------------------
   Junior Festival polish layer loader.
   Kept as separate theme assets rather than the page template's custom_css,
   because a save in the Shopify theme editor rewrites that template and has
   already reverted these fixes once.

   CACHE KEY. Shopify's CDN serves a stale body from a bare asset URL, so the
   polish stylesheets need a query token. This used to be an hourly clock
   value, which meant an edit made at 10:05 did not appear until 11:00 and
   looked like it had not been made. POLISH_REV is a manual token instead:
   bump it in the same commit as any edit to junior-festival-polish.css or
   junior-festival-legacy-polish.css and the new body is served immediately,
   because this file's own URL is content hashed by Shopify. Between bumps the
   stylesheets cache normally.
   --------------------------------------------------------------------------- */
(function () {
  var EXTRAS = ['junior-festival-polish.css', 'junior-festival-legacy-polish.css'];
  var POLISH_REV = '2026-08-20a';

  function load() {
    var base = document.querySelector('link[rel="stylesheet"][href*="junior-festival.css"]');
    if (!base || base.dataset.polishLoaded) return !!base;
    base.dataset.polishLoaded = '1';

    var anchor = base;
    EXTRAS.forEach(function (name) {
      var href = base.getAttribute('href').split('?')[0]
                     .replace('junior-festival.css', name) + '?v=' + POLISH_REV;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      anchor.parentNode.insertBefore(link, anchor.nextSibling);
      anchor = link;
    });
    return true;
  }

  if (!load()) {
    document.addEventListener('DOMContentLoaded', load);
  }
})();

/* ---------------------------------------------------------------------------
   Junior Festival hero darkening: REMOVED, on purpose.

   This file used to force the hero veil to a minimum opacity of 0.92 plus a
   hard coded gradient, both as inline styles carrying !important. It was added
   for one page under time pressure and then applied to every page that renders
   .jf-hero__veil, so the theme editor's Background darkening slider had nothing
   left to control and a dark photo composited to near black.

   The darkening now lives in assets/junior-festival-polish.css:
     .jf-hero__bg::after  fixed legibility floor, sized from measured contrast
     .jf-hero__veil       gradient shape only, strength from the slider

   Nothing here writes to the veil any more. Do not reintroduce it. If a hero
   needs to be darker, move that page's Background darkening slider.
   --------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
   NPC union crests in the fixture strip.
   The strip renders a text disc (BOP, TAR, CAN) as the base state. This swaps
   in the real union crest from a single sprite sheet, matched off the team
   name next to the disc. Progressive enhancement: if the sprite fails to load
   or the team is not one of the 14 unions, the initials disc stays exactly as
   it is, so nothing can end up blank.
   --------------------------------------------------------------------------- */
(function () {
  var SPRITE = '/cdn/shop/files/npc-crest-sprite-v2.png';
  var UNIONS = ['auckland', 'bay-of-plenty', 'canterbury', 'counties-manukau',
                'hawkes-bay', 'manawatu', 'north-harbour', 'northland', 'otago',
                'southland', 'taranaki', 'tasman', 'waikato', 'wellington'];

  function slug(name) {
    return (name || '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/['‘’ʻ`]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function indexFor(name) {
    var s = slug(name);
    var i = UNIONS.indexOf(s);
    if (i !== -1) return i;
    for (var j = 0; j < UNIONS.length; j++) {
      if (s.indexOf(UNIONS[j]) === 0 || UNIONS[j].indexOf(s) === 0) return j;
    }
    return -1;
  }

  function paint() {
    var discs = document.querySelectorAll('.rh__evt-disc:not([data-crest])');
    if (!discs.length) return;

    discs.forEach(function (disc) {
      var team = disc.parentElement && disc.parentElement.querySelector('.rh__evt-tname');
      var i = indexFor(team ? team.textContent : '');
      if (i === -1) return;

      disc.setAttribute('data-crest', UNIONS[i]);
      disc.style.backgroundImage = 'url(' + SPRITE + ')';
      disc.style.backgroundSize = (UNIONS.length * 100) + '% 100%';
      disc.style.backgroundPosition = (i / (UNIONS.length - 1) * 100) + '% 50%';
      disc.style.backgroundRepeat = 'no-repeat';
      disc.style.backgroundColor = 'transparent';
      disc.style.border = '0';
      disc.style.color = 'transparent';
      disc.style.fontSize = '0';
    });
  }

  function start() {
    var probe = new Image();
    probe.onload = function () {
      paint();
      // The strip is a carousel and can rerender, so keep new cards covered.
      var strip = document.querySelector('.rh__strip-track') || document.body;
      if (window.MutationObserver) {
        new MutationObserver(paint).observe(strip, { childList: true, subtree: true });
      }
    };
    probe.src = SPRITE;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();


/* Mobile next game bar. Slides up once the header has scrolled away, steps aside near the
   footer, and stays closed for the visit once dismissed. */
(function () {
  var bar = document.getElementById('rh-nextbar');
  if (!bar) return;
  try { if (sessionStorage.getItem('rhNextbarClosed') === '1') return; } catch (e) {}
  var mq = window.matchMedia('(max-width: 749px)');
  var shown = false, queued = false;
  bar.hidden = false;
  bar.inert = true;
  function update() {
    queued = false;
    var doc = document.documentElement;
    var want = window.scrollY > (mq.matches ? 320 : 600) &&
      !doc.classList.contains('rh-drawer-open') &&
      window.innerHeight + window.scrollY < doc.scrollHeight - 200;
    if (want === shown) return;
    shown = want;
    bar.classList.toggle('is-in', want);
    bar.inert = !want;
  }
  function queue() { if (!queued) { queued = true; window.requestAnimationFrame(update); } }
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue, { passive: true });
  var close = bar.querySelector('.rh-nextbar__close');
  if (close) close.addEventListener('click', function () {
    bar.hidden = true;
    try { sessionStorage.setItem('rhNextbarClosed', '1'); } catch (e) {}
  });
  update();
})();
